import { yupResolver } from "@hookform/resolvers/yup";
import { StackScreenProps } from "@react-navigation/stack";
import { CardStyleInterpolators } from "@react-navigation/stack";
import {
  LayoutNodeInput,
  LayoutNodeType,
  Product,
} from "@towersystems/roam-common/lib/generated-types";
import { FormProvider, useForm } from "react-hook-form";
import { Colors } from "react-native-ui-lib";
import { ModalCloseButton } from "@towersystems/roam-common-native";
import { ProductListScreen } from "../../../../components/ProductListScreen";
import { ProductListScreenContext } from "../../../../components/ProductListScreen/context";
import { CreateProductQuickKeyRootParamList } from "../../../../types";
import { useCreateTile } from "../../../../utilities/layout";
import {
  defaultLayoutNodeInputValues,
  productNodeInputSchema,
} from "../../../../utilities/layout/constants";
import { createStackHeaderNavigator } from "@towersystems/roam-common-native";
import { CreateProductQuickKeyScreen } from "../CreateProductQuickKeyScreen";
import ProductModel from "../../../../utilities/data/model/Product";
import { ProductsFilterValues } from "../../../products/types";
import { defaultProductFilterValues } from "../../../products/common";
import { ListFilterProvider } from "../../../../utilities/list-filter/use-list-filter-context";
import { useListFilter } from "../../../../utilities/list-filter";
import { ProductListFilterRootScreen } from "../../../products/screens/ProductListFilterRootScreen";
import { GenerateAiIconRootScreen } from "../GenerateAiIconScreens";
import { ColorPickerScreen } from "../../components/ColorPickerScreen/ColorPickerScreen";
import { GenerateIconScreenContext } from "../GenerateAiIconScreens/common";

export type CreateProductQuickKeyRootScreenProps = StackScreenProps<
  CreateProductQuickKeyRootParamList,
  "CreateProductQuickKeyScreen"
>;

export function CreateProductQuickKeyRootScreen() {
  const filterContext = useListFilter<ProductsFilterValues>({
    defaultValues: defaultProductFilterValues,
  });

  const Stack =
    createStackHeaderNavigator<CreateProductQuickKeyRootParamList>();

  const { config } = useCreateTile();

  const methods = useForm<LayoutNodeInput>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      ...defaultLayoutNodeInputValues,
      ...config,
      type: LayoutNodeType.PRODUCT,
    },
    resolver: yupResolver(productNodeInputSchema),
  });

  const { setValue } = methods;

  function handleProductSelect(product: Product | ProductModel) {
    setValue("productId", product.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  return (
    <GenerateIconScreenContext.Provider
      value={{
        onSubmit: async (base64Image: string) => {
          const time = new Date().getTime();
          const id = `${time}-${Math.floor(Math.random() * 1000)}`;

          // Pass the generated icon back to the previous screen
          const name = `key-icon-${id}.png`;
          setValue(
            "base64Image",
            {
              base64: base64Image,
              imageName: name,
            },
            {
              shouldDirty: true,
              shouldValidate: true,
            }
          );
        },
      }}
    >
      <ListFilterProvider {...filterContext}>
        <FormProvider {...methods}>
          <ProductListScreenContext.Provider
            value={{
              onSelect: handleProductSelect,
            }}
          >
            <Stack.Navigator
              screenOptions={{
                animationEnabled: true,
              }}
            >
              <Stack.Group
                screenOptions={{
                  cardStyleInterpolator:
                    CardStyleInterpolators.forHorizontalIOS,
                  title: "",
                }}
              >
                <Stack.Screen
                  name="CreateProductQuickKeyScreen"
                  component={CreateProductQuickKeyScreen}
                />
                <Stack.Group
                  screenOptions={{
                    headerShown: true,
                    headerLeft: () => <ModalCloseButton type="Back" />,
                  }}
                >
                  <Stack.Screen
                    name="ProductListScreen"
                    component={ProductListScreen}
                  />
                  <Stack.Screen
                    name="GenerateAiIconRootScreen"
                    component={GenerateAiIconRootScreen}
                    options={{
                      title: "",
                      headerShown: false,
                    }}
                  />
                  <Stack.Screen
                    name="ColorPickerScreen"
                    component={ColorPickerScreen}
                    options={{
                      title: "",
                    }}
                  />
                </Stack.Group>
              </Stack.Group>
              <Stack.Group
                screenOptions={{
                  headerShown: false,
                  presentation: "modal",
                }}
              >
                <Stack.Screen
                  name="ProductListFilterRootScreen"
                  component={ProductListFilterRootScreen}
                />
              </Stack.Group>
            </Stack.Navigator>
          </ProductListScreenContext.Provider>
        </FormProvider>
      </ListFilterProvider>
    </GenerateIconScreenContext.Provider>
  );
}
